import { saveAs } from 'file-saver';
import { ToastrService } from 'ngx-toastr';
import { Observable } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';

import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { ISnListQueryParameters } from '@lib/api';
import { ISnAuditListResponse, SnAudit } from '@lib/api/entities';
import { t } from '@lib/translation';
import { SnHttpResponseFilter } from '@lib/ui/filter';

export type DisclaimerType = 'AXON' | 'CJIS';

@Injectable({ providedIn: 'root' })
export class SnAuditService {
  constructor(
    private readonly _httpClient: HttpClient,
    private readonly _toastrService: ToastrService,
  ) {}

  public getAuditList(params: ISnListQueryParameters): Observable<ISnAuditListResponse> {
    return this._httpClient.get('/api/audit/', { params }).pipe(
      map((response: any) => ({
        count: response.count,
        totalPages: response.totalPages,
        results: response.results.data.map((audit) => new SnAudit(audit)),
        filters: response.results.facetFields.map((filter) => new SnHttpResponseFilter(filter)),
      })),
    );
  }

  public getAudit(id: string): Observable<SnAudit> {
    return this._httpClient
      .get(`/api/audit/${id}/`)
      .pipe(map((response: Object) => new SnAudit(response)));
  }

  public updateAuditStatus(id: string): Observable<SnAudit> {
    return this._httpClient
      .post(`/api/audit/status_update/${id}/`, null)
      .pipe(map((response: Object) => new SnAudit(response)));
  }

  public bulkStatusUpdate(audits: SnAudit[]): Observable<SnAudit[]> {
    return this._httpClient
      .post(
        '/api/audit/status_update/bulk/',
        audits.map((a) => ({ id: a.id })),
      )
      .pipe(map((response: any) => response.map((item) => new SnAudit(item))));
  }

  public createAudit(data: any[]): Observable<SnAudit[]> {
    data = data.map((d) => {
      return {
        changeData: {},
        ...d,
      };
    });
    return this._httpClient.post('/api/audit/', data).pipe(
      map((response: any) => {
        return response.map((item) => new SnAudit(item));
      }),
    );
  }

  public createDisclaimerAudit(data: {
    userId: string;
    userName: string;
    disclaimer: DisclaimerType;
  }) {
    const detailValueMap: Record<DisclaimerType, string> = {
      AXON: 'AXON_DISCLAIMER',
      CJIS: 'CJIS_DISCLAIMER',
    };
    const detailValue = detailValueMap[data.disclaimer];

    return this.createAudit([
      {
        object: {
          id: data.userId,
          name: data.userName,
          contentType: 'User',
        },
        operation: 'CONFIRMATION',
        detail: {
          value: detailValue,
        },
      },
    ]);
  }

  public createAuditNote(auditId: string, notes: any): any {
    return this._httpClient.post(`/api/audit/${auditId}/notes/`, notes).pipe(
      tap({
        next: () => this._toastrService.success(t('audit.noteCreated')),
        error: () => this._toastrService.error(t('audit.couldNotCreateNote')),
      }),
    );
  }

  public addAuditCaseNumber(auditId: string, caseNumber: any): any {
    return this._httpClient.post(`/api/audit/${auditId}/incident_id/`, caseNumber).pipe(
      tap({
        next: () => this._toastrService.success(t('audit.caseNumberAdded')),
        error: (err: HttpErrorResponse) => this._toastrService.error(`${err.error.detail}`),
      }),
    );
  }

  public exportAudit(data: any = {}): Observable<Object> {
    return this._httpClient.get('/api/audit/export/', { responseType: 'blob', params: data }).pipe(
      catchError(() => this._httpClient.post('/api/audit/export/', data, { responseType: 'blob' })),
      tap({
        next: (response: BlobPart) => {
          const blob = new Blob([response], { type: 'text/csv' });
          saveAs(blob, `Audit-export-${Date.now()}.csv`);
        },
        error: (errorResponse: HttpErrorResponse) => {
          if (errorResponse.status === 400 && errorResponse.error.msg) {
            this._toastrService.error(errorResponse.error.msg);
          }
        },
      }),
    );
  }
}
